import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import type { ILocation, ICreateLocationRequest, IUpdateLocationRequest } from '@map-bldr/shared';

export function useLocations() {
  return useQuery<ILocation[]>({
    queryKey: ['locations'],
    queryFn: () => apiClient.get<ILocation[]>('/locations'),
  });
}

export function useCreateLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ICreateLocationRequest) => {
      return apiClient.post<ILocation>('/locations', data);
    },
    onMutate: async (data: ICreateLocationRequest) => {
      queryClient.setQueryData<ILocation[]>(['locations'], (old = []) => [data, ...old]);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
    },
  });
}

export interface IUpdateLocationParams {
  locationId: string;
  data: IUpdateLocationRequest;
}

export function useUpdateLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ locationId, data }: IUpdateLocationParams) =>
      apiClient.patch<ILocation>(`/locations/${locationId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
    },
  });
}

export function useDeleteLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (locationId: string) =>
      apiClient.delete<void>(`/locations/${locationId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
    },
  });
}
